'use client';

import { useState } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AlertTitle from '@mui/material/AlertTitle';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';

import { useBoolean } from 'src/hooks/use-boolean';

import { Iconify } from 'src/components/iconify';
import { toast } from 'src/components/snackbar';

import { ORGANISATIONS_TYP_LABELS } from 'src/utils/format-organisation-type';

import type { IOrganisation, OrganisationsTyp, ISkapaOrganisationRequest } from 'src/types/nya/common';

import { SkapaOrganisation } from './OrganisationsService';
import {
  useOrganisationsVerifiering,
  formateraOrganisationsnummer,
} from './useOrganisationsVerifiering';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (nyskapad?: IOrganisation) => void;
};

// Läses ur etikettkatalogen i stället för en egen lista, som både innehöll en
// verksamhetsinriktning som typ och saknade IDEELL_FÖRENING.
const ORGANISATIONS_TYPER: { value: OrganisationsTyp; label: string }[] = (
  Object.entries(ORGANISATIONS_TYP_LABELS) as [OrganisationsTyp, string][]
).map(([value, label]) => ({ value, label }));

const innevarandeÅr = new Date().getFullYear();

const tomtFormulär = {
  organisationsNamn: '',
  organisationsTyp: 'BOSTADSRÄTTSFÖRENING' as OrganisationsTyp,
  momsregistreringsnummer: '',
  förstaDag: `${innevarandeÅr}-01-01`,
  sistaDag: `${innevarandeÅr}-12-31`,
};

/**
 * Lägger till en organisation utanför onboardingen.
 *
 * <p>Namnet skrevs tidigare fritt i det här formuläret, precis som i onboardingen — och det var så
 * "Kråkan" hamnade på Bolund Ink AB:s organisationsnummer. Numret slås numera upp först, och
 * Bolagsverkets svar är det som gäller. Regeln ligger i {@link useOrganisationsVerifiering}, delad
 * med de andra ytorna, så att den inte kan glida isär mellan dem.
 */
export default function LäggTillOrganisationDialog({ open, onClose, onCreated }: Props) {
  const sparar = useBoolean();
  const verifiering = useOrganisationsVerifiering();

  const [form, setForm] = useState(tomtFormulär);

  const uppdatera = (fält: keyof typeof form) => (värde: string) =>
    setForm((prev) => ({ ...prev, [fält]: värde }));

  const stäng = () => {
    setForm(tomtFormulär);
    verifiering.setOrganisationsnummer('');
    verifiering.återställ();
    sparar.onFalse();
    onClose();
  };

  const handleVerifiera = async () => {
    const status = await verifiering.verifiera();
    if (status === 'VERIFIERAD') {
      toast.success('Organisationen hittades hos Bolagsverket.');
    }
  };

  // Namn och juridisk form kommer från registret när det finns ett svar där. Formulärets egna
  // värden används bara för en organisation Bolagsverket inte känner.
  const namn = verifiering.namnetÄrLåst
    ? (verifiering.uppgifter?.organisationsNamn ?? '')
    : form.organisationsNamn;
  const typ = verifiering.namnetÄrLåst
    ? (verifiering.uppgifter?.organisationsTyp ?? form.organisationsTyp)
    : form.organisationsTyp;

  const handleSpara = async () => {
    if (!verifiering.fårSkapas) return;
    if (!namn.trim()) {
      toast.error('Organisationen behöver ett namn.');
      return;
    }
    sparar.onTrue();
    try {
      const request: ISkapaOrganisationRequest = {
        organisationsNummer: formateraOrganisationsnummer(verifiering.organisationsnummer),
        organisationsNamn: namn.trim(),
        organisationsTyp: typ,
        momsregistreringsnummer: form.momsregistreringsnummer.trim() || undefined,
        räkenskapsår:
          form.förstaDag && form.sistaDag
            ? { förstaDag: form.förstaDag, sistaDag: form.sistaDag }
            : undefined,
      };
      const nyskapad = await SkapaOrganisation(request);
      toast.success('Organisationen har lagts till.');
      onCreated(nyskapad);
      stäng();
    } catch (error) {
      console.error(error);
      // Backends meddelande bär åtgärden (409 hänvisar till "Gå med"); visa det som det är.
      const meddelande = (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(meddelande ?? 'Något gick fel när organisationen skulle sparas.');
    } finally {
      sparar.onFalse();
    }
  };

  return (
    <Dialog open={open} onClose={stäng} fullWidth maxWidth="sm">
      <DialogTitle>Lägg till organisation</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Slå upp hos Bolagsverket
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Organisationsnummer"
                placeholder="769600-0000"
                value={verifiering.organisationsnummer}
                onChange={(e) => verifiering.ändraOrganisationsnummer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleVerifiera();
                }}
                error={Boolean(verifiering.felmeddelande)}
                helperText={verifiering.felmeddelande}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                loading={verifiering.verifierar}
                onClick={handleVerifiera}
                sx={{ flexShrink: 0 }}
              >
                Verifiera
              </Button>
            </Stack>
          </Box>

          {verifiering.status === 'VERIFIERAD' && verifiering.uppgifter && (
            <Alert severity="success" variant="outlined" icon={<Iconify icon="solar:verified-check-bold-duotone" />}>
              <AlertTitle sx={{ mb: 0.5 }}>{verifiering.uppgifter.organisationsNamn}</AlertTitle>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {verifiering.uppgifter.organisationsTyp && (
                  <Chip
                    size="small"
                    variant="soft"
                    label={ORGANISATIONS_TYP_LABELS[verifiering.uppgifter.organisationsTyp]}
                  />
                )}
                {verifiering.uppgifter.adress?.ort && (
                  <Chip size="small" variant="soft" label={verifiering.uppgifter.adress.ort} />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Uppgifterna kommer från Bolagsverket och går därför inte att ändra här.
              </Typography>
            </Alert>
          )}

          {verifiering.status === 'OKÄNT_NUMMER' && (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>Numret finns inte hos Bolagsverket</AlertTitle>
              <Typography variant="body2" color="text.secondary">
                Samfälligheter registreras hos Lantmäteriet och ideella föreningar hos
                Skatteverket. Du kan lägga till organisationen ändå, men den kommer märkas som
                obekräftad och kan då inte ta emot ansökningar om tillgång.
              </Typography>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={verifiering.okäntNummerBekräftat}
                    onChange={(e) => verifiering.setOkäntNummerBekräftat(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Min organisation finns inte hos Bolagsverket
                  </Typography>
                }
              />
            </Alert>
          )}

          {verifiering.status === 'FEL' && (
            <Alert severity="error" variant="outlined">
              Vi kunde inte nå Bolagsverket och kan därför inte bekräfta organisationen. Försök
              igen om en stund.
            </Alert>
          )}

          {/* Namn och form skrivs bara när Bolagsverket inte har något att säga om numret. */}
          {verifiering.status === 'OKÄNT_NUMMER' && verifiering.okäntNummerBekräftat && (
            <>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  Uppgifter
                </Typography>
              </Divider>

              <TextField
                fullWidth
                required
                label="Organisationsnamn"
                value={form.organisationsNamn}
                onChange={(e) => uppdatera('organisationsNamn')(e.target.value)}
              />

              <TextField
                select
                fullWidth
                label="Typ"
                value={form.organisationsTyp}
                onChange={(e) => uppdatera('organisationsTyp')(e.target.value)}
              >
                {ORGANISATIONS_TYPER.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          {verifiering.fårSkapas && (
            <>
              <TextField
                fullWidth
                label="Momsregistreringsnummer"
                placeholder="SE769600000001"
                value={form.momsregistreringsnummer}
                onChange={(e) => uppdatera('momsregistreringsnummer')(e.target.value)}
              />

              <Divider>
                <Typography variant="caption" color="text.secondary">
                  Räkenskapsår
                </Typography>
              </Divider>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Första dag"
                  value={form.förstaDag}
                  onChange={(e) => uppdatera('förstaDag')(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Sista dag"
                  value={form.sistaDag}
                  onChange={(e) => uppdatera('sistaDag')(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={stäng}>
          Avbryt
        </Button>
        <Button
          variant="contained"
          loading={sparar.value}
          disabled={!verifiering.fårSkapas}
          onClick={handleSpara}
        >
          Lägg till
        </Button>
      </DialogActions>
    </Dialog>
  );
}
